import { useAuth } from '@/contexts/AuthContext';

/**
 * Permission helpers — read effective_permissions from useAuth().user.
 *
 * Backend endpoint /api/auth/me returns `permissions` as a flat dict
 * { perm_key: bool }, owners get every key = true.
 *
 * Examples:
 *   const { can, isOwner } = usePermissions();
 *   if (can('create_jobs')) ...
 *   if (isOwner) ...
 *
 *   <Can perm="delete_jobs">
 *     <Button>Delete</Button>
 *   </Can>
 *
 *   // Show fallback when not allowed:
 *   <Can perm="aspire_config" fallback={<p>Read-only</p>}>...</Can>
 *
 * iter-166 — legacy tier flag `isAdmin` REMOVED. Gate on granular
 * `can('<perm>')` (owners pass automatically) or `isOwner` for
 * owner-only actions. `isOwner`/`hasRole` added so the Pass-A codemod can replace inline
 * `user?.role === 'admin'` / `user?.role === 'owner'` snippets without
 * changing semantics. Source of truth for what an "admin" is now
 * lives here — bug-fix once, fix everywhere.
 */
export function usePermissions() {
  const { user } = useAuth();
  const perms = (user && user.permissions) || {};
  const role = user?.role || null;
  const isOwner = role === 'owner';
  const isPlatformAdmin = !!user?.is_platform_admin;
  // Platform STAFF are not admins, but their defining capability is
  // impersonate-into-tenant. Anyone holding `impersonate_tenants` (staff,
  // support_admin, super_admin) must be able to switch between the companies
  // they were granted — that's what powers the top-bar TenantSwitcher.
  const platformCaps = (user && user.platform_capabilities) || {};
  const canImpersonateTenants = !!platformCaps.impersonate_tenants || isPlatformAdmin;

  const can = (perm) => {
    if (!perm) return true;
    if (isOwner) return true;
    if (Array.isArray(perm)) return perm.every((p) => !!perms[p]);
    return !!perms[perm];
  };

  const canAny = (...permList) => {
    if (isOwner) return true;
    return permList.flat().some((p) => !!perms[p]);
  };

  const hasRole = (...roles) => roles.flat().includes(role);

  return {
    can,
    canAny,
    hasRole,
    permissions: perms,
    role,
    isOwner,
    isPlatformAdmin,
    platformCaps,
    canImpersonateTenants,
    userId: (user && (user.id || user._id)) || null,
  };
}

export function Can({ perm, anyOf, fallback = null, children }) {
  const { can, canAny } = usePermissions();
  const allowed = anyOf ? canAny(anyOf) : can(perm);
  if (!allowed) return fallback;
  return children;
}

// Field/mobile-only permission keys (incl. the mobile alias keys the /auth
// response emits). A user whose ONLY granted permissions are in this set is
// "clock-only" — they have no office/desk surface to land on.
const FIELD_ONLY_PERMS = new Set([
  'time_clock.clock', 'time_clock.crew',
  'view_material_requests',
  'morning_meeting.join',
  'view_relay', 'relay_view', 'view_driver_list', 'view_daily_routes',
]);

/**
 * isClockOnlyUser — true when a user can clock in but has NO office access
 * (every other permission they hold is a field/mobile one). These users are
 * auto-sent straight to the mobile /clock screen after login instead of the
 * full app. Owners, admins and platform staff are never clock-only. Erring
 * toward "not clock-only" is safe (they simply aren't auto-redirected).
 */
export function isClockOnlyUser(user) {
  if (!user) return false;
  if (user.role === 'owner' || user.role === 'admin') return false;
  if (user.is_platform_admin) return false;
  const perms = user.permissions || {};
  if (!perms['time_clock.clock']) return false;
  const hasDeskAccess = Object.keys(perms).some((k) => perms[k] && !FIELD_ONLY_PERMS.has(k));
  return !hasDeskAccess;
}

export default Can;
